import { gql, useLazyQuery } from '@apollo/client'
import XIcon from '@mui/icons-material/X'
import { Badge, Box, Stack, Tooltip } from '@mui/material'
import Button from '@mui/material/Button'
import {
  CampaignChannel,
  Query,
  Retailer,
} from '@promoboxx/graphql-gateway-types'
import useFilter from '@promoboxx/use-filter'
import I18n from 'i18n-js'
import moment, { Moment, MomentInput } from 'moment'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'

import RequestCampaignsDialog from '@src/components/content/RequestCampaignsDialog'
import urlParamStore from '@src/lib/urlParamStore'
import { useFeatureFlag } from '@src/lib/useFeatureFlag'
import useOnRetailerAccountChange from '@src/lib/useOnRetailerAccountChange'
import usePreviouslySelectedBrand from '@src/lib/usePreviouslySelectedBrand'

import useModalState from '../../lib/useModalState'
import { currentRetailerAccountSelector } from '../../stores/app/selectors'
import { currentRetailerIdSelector } from '../../stores/retailers/selectors'
import LegacySvg from '../shared/LegacySvg'

import {
  CampaignCardActionAreaDot,
  CampaignCardTruncatedDescription,
} from './CampaignCard'
import CampaignResultsContainer from './CampaignResultsContainer'
import ContentFilters from './ContentFilters'
import { OmniCampaignCardProps } from './OmniCampaignCard'
import organicCampaignPaths from './organicCampaignPaths'

type NormalizedChannels = {
  slug: string
  type: CampaignChannel['channel']
  to: string
  count: number
}

const normalizeChannelType = (channelName: string) => {
  switch (channelName) {
    case 'asset':
      return 'downloadables'
    case 'embed':
      return 'banner'
    default:
      return channelName
  }
}

export const GET_CAMPAIGNS_SEARCH = gql`
  query GetCampaignsSearchContentPage(
    $resourceType: CampaignSearchResourceType!
    $resourceId: ID!
    $params: CampaignSearchParams
  ) {
    getCampaignSearch(
      resourceType: $resourceType
      resourceId: $resourceId
      params: $params
    ) {
      data {
        id
        name
        start_date
        end_date
        brand {
          id
          logo_url
          slug
        }
        creative_path
        description
        logo_public_id
        invitation {
          id
          state
          created_at
          status
        }
        socials {
          channel
          count
        }
        localAdBalances {
          id
          local_ads_balance
        }
      }
      paging {
        total_results
      }
    }
  }
`

const OrganicCampaigns: React.FC<
  React.PropsWithChildren<{
    brandFilter?: string
    filterBarRef: HTMLDivElement | null
  }>
> = ({ filterBarRef, brandFilter }) => {
  const pageSize = 24
  const retailerAccount = useSelector(currentRetailerAccountSelector)
  const [campaignCards, setCampaignCards] = useState<OmniCampaignCardProps[]>(
    [],
  )
  const retailerId = useSelector(currentRetailerIdSelector)
  const { isOpen, toggleModal } = useModalState()
  const [getCampaignSearch, { data, refetch, called }] =
    useLazyQuery<Query>(GET_CAMPAIGNS_SEARCH)
  const isShareFlowUpliftEnabled = useFeatureFlag('share-flow-uplift')

  const defaultRetailerIds = retailerAccount.retailers?.map(
    (retailer: Retailer) => {
      if (brandFilter === 'brand') {
        return !retailer.brand?.community ? retailer.id : ''
      } else if (brandFilter === 'industry') {
        return retailer.brand?.community ? retailer.id : ''
      }
      return retailer.id
    },
  )

  const { previouslySelectedRetailers, setPreviouslySelectedRetailer } =
    usePreviouslySelectedBrand(undefined, brandFilter)

  const { filterInfo, updateFilter, setPage, resetFilter, isLoading } =
    useFilter('content.organic_campaigns', {
      shouldForceRunOnMount: true,
      defaultFilterInfo: {
        filter: {
          brands: previouslySelectedRetailers.map((x) => x.id),
          filterByChannel: [],
          showValue: [],
          tagsValues: [],
          searchValue: null,
        },
        pageSize,
      },
      onChange(filterInfo) {
        let params = {
          per_page:
            !called && filterInfo.offset
              ? filterInfo.offset + pageSize
              : pageSize,
          offset: !called && filterInfo.offset ? 0 : filterInfo.offset,
          retailer_ids: defaultRetailerIds,
          tags: [],
          search: null,
          filterParams: {
            show_state: undefined,
          },
        }

        if (filterInfo.filter.brands && filterInfo.filter.brands.length) {
          params.retailer_ids = filterInfo.filter.brands
        }
        if (
          filterInfo.filter.tagsValues &&
          filterInfo.filter.tagsValues.length
        ) {
          params.tags = filterInfo.filter.tagsValues
        }
        if (filterInfo.filter.searchValue) {
          params.search = filterInfo.filter.searchValue
        }
        if (
          filterInfo.filter.filterByChannel?.length &&
          filterInfo.filter.filterByChannel[0]
        ) {
          params.filterParams = filterInfo.filter.filterByChannel.reduce(
            (acc, f: string) => Object.assign(acc, { [f]: true }),
            { ...params.filterParams },
          )
        }
        if (
          filterInfo.filter.showValue?.length &&
          filterInfo.filter.showValue[0] !== '0'
        ) {
          params.filterParams.show_state = filterInfo.filter.showValue[0]
        }

        const variables = {
          resourceId:
            retailerId && !params.retailer_ids
              ? retailerId
              : retailerAccount.id,
          resourceType:
            retailerId && !params.retailer_ids
              ? 'retailers'
              : 'retailer_accounts',
          params,
        }

        return (called ? refetch(variables) : getCampaignSearch({ variables }))
          .then((response) => {
            return {
              filterInfo: {
                totalResults:
                  response?.data?.getCampaignSearch?.paging?.total_results || 0,
              },
            }
          })
          .catch((error) => {
            console.error(error)
          })
      },
      store: urlParamStore,
    })

  useEffect(() => {
    setPreviouslySelectedRetailer(filterInfo.filter.brands)
  }, [filterInfo.filter.brands, setPreviouslySelectedRetailer])

  useEffect(() => {
    if (!isLoading && data?.getCampaignSearch?.data) {
      const now = moment()

      // Normalize the data we get back into results
      const results =
        data?.getCampaignSearch?.data?.map((campaign) => {
          const channels: Array<NormalizedChannels> = []
          campaign?.socials?.forEach((social) => {
            const channel = normalizeChannelType(social?.channel || '')
            if (!social?.channel) return
            channels.push({
              slug: campaign?.brand?.slug || '',
              type: normalizeChannelType(social?.channel) || '',
              to: isShareFlowUpliftEnabled
                ? organicCampaignPaths.details.build({
                    retailerAccountSlug: retailerAccount.slug,
                    campaignId: campaign?.creative_path || '',
                    channel,
                  })
                : `${organicCampaignPaths.legacyDetails.build({
                    retailerAccountSlug: retailerAccount.slug,
                    brandSlug: campaign?.brand?.slug || '',
                    campaignId: campaign?.creative_path || '',
                    channel,
                  })}${window.location.search}`,
              count: social?.count || 0,
            })
          })

          // const localAdBalance =
          //   campaign?.localAdBalances?.reduce((acc: number, lab) => {
          //     return acc + lab?.local_ads_balance
          //   }, 0) || 0

          const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
          const isNew =
            ((campaign?.invitation?.state === 'engaged' &&
              campaign?.invitation?.status !== 'accepted') ||
              campaign?.invitation?.state !== 'engaged') &&
            new Date(campaign?.invitation?.created_at || '') >= sevenDaysAgo

          const omniCampaignCard: OmniCampaignCardProps = {
            id: campaign.id,
            title: campaign.name || '',
            subheader: formatDaysRemainingCopy(now, moment(campaign.end_date)),

            description: (
              <CampaignCardTruncatedDescription>
                {campaign.description}
              </CampaignCardTruncatedDescription>
            ),

            href: isShareFlowUpliftEnabled
              ? organicCampaignPaths.details.build({
                  retailerAccountSlug: retailerAccount.slug,
                  campaignId: campaign?.creative_path || '',
                })
              : organicCampaignPaths.legacyDetails.build({
                  retailerAccountSlug: retailerAccount.slug,
                  brandSlug: campaign?.brand?.slug || '',
                  campaignId: campaign?.creative_path || '',
                }),
            isLegacyLink: !isShareFlowUpliftEnabled,

            actionAreaLabel: (
              <>
                <CampaignCardActionAreaDot />

                <span>
                  {campaign?.invitation?.state === 'engaged'
                    ? I18n.t('content.viewCampaign')
                    : I18n.t('content.activateCampaign')}
                </span>
              </>
            ),
            actionAreaColor: 'pbxxCyan',
            actionAreaInvert: campaign?.invitation?.state === 'engaged',

            sashLabel: isNew ? I18n.t('shared.new') : undefined,
            sashColor: 'discovery',
            sashTooltip: I18n.t('content.newSashTooltip'),

            media: [{ publicId: campaign.logo_public_id }],
            brandLogo: campaign?.brand?.logo_url,

            secondaryContent: (
              <>
                <Stack direction="row" spacing={3}>
                  {channels.map((x) => {
                    return (
                      <Tooltip
                        title={I18n.t('content.channelIconBadge', {
                          count: x.count,
                          type:
                            x.type.charAt(0).toUpperCase() + x.type.slice(1),
                        })}
                      >
                        <Badge badgeContent={x.count} color="secondary" max={9}>
                          <a
                            href={x.to}
                            onClick={(event) => {
                              event.stopPropagation()
                            }}
                          >
                            {x.type === 'twitter' ? (
                              <XIcon
                                sx={{
                                  backgroundColor: '#000',
                                  color: '#FFF',
                                  borderRadius: '50%',
                                  width: '24px',
                                  height: '24px',
                                }}
                              />
                            ) : (
                              <LegacySvg
                                height="24px"
                                symbol={x.type}
                                width="24px"
                              />
                            )}
                          </a>
                        </Badge>
                      </Tooltip>
                    )
                  })}
                </Stack>
              </>
            ),
          }

          return omniCampaignCard
        }) || []
      setCampaignCards((prev) => {
        return [...prev, ...results]
      })
    }
  }, [isLoading, data, retailerAccount, isShareFlowUpliftEnabled])
  const clearAndReset = () => {
    resetFilter()
    setCampaignCards([])
  }
  useOnRetailerAccountChange(clearAndReset)

  return filterBarRef ? (
    <>
      {createPortal(
        <ContentFilters
          brandFilter={brandFilter}
          filterInfo={filterInfo}
          onClear={clearAndReset}
          onChange={(key, value) => {
            updateFilter({ [key]: value })
            setCampaignCards([])
          }}
        />,
        filterBarRef,
        'organic-campaigns-filter-bar',
      )}

      <CampaignResultsContainer
        pageSize={pageSize}
        isLoading={isLoading}
        results={campaignCards}
        showMoreHandler={() => {
          setPage(filterInfo.page + 1)
        }}
        resetFilters={clearAndReset}
        totalResults={filterInfo.totalResults}
        noResultsText={I18n.t('content.noCampaignsAtAll')}
      >
        {!isLoading &&
          (filterInfo.totalResults === campaignCards.length ||
            campaignCards.length === 0) && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Button
                color="secondary"
                onClick={toggleModal}
                size="large"
                variant="outlined"
              >
                {I18n.t('content.requestMore')}
              </Button>
            </Box>
          )}
      </CampaignResultsContainer>

      <RequestCampaignsDialog
        onClose={toggleModal}
        open={isOpen}
        total={filterInfo.totalResults}
      />
    </>
  ) : null
}

const formatDaysRemainingCopy = (now: MomentInput, endDate: Moment) => {
  let daysRemaining = endDate.diff(now, 'days')

  if (Number.isNaN(daysRemaining)) {
    return null
  }

  return endDate.calendar(null, {
    sameDay: `[${I18n.t('content.endsToday')}] LT`,
    nextDay: `[${I18n.t('content.endsTomorrow')}] LT`,
    nextWeek: `[${I18n.t('content.xDaysRemaining', {
      count: daysRemaining,
    })}]`,
    sameElse: `[${I18n.t('content.xDaysRemaining', {
      count: daysRemaining,
    })}]`,
  })
}

export default OrganicCampaigns
